import { RefObject, useEffect, useRef } from 'react';

// dragThresholdPx 拖拽判定阈值: 整行都是拖拽热区, 手抖几像素不该被当成一次排序
const dragThresholdPx = 4;

/**
 * 整行拖拽排序 (指针事件实现, 不用 HTML5 原生拖放: 移动端/样式可控).
 * box 内匹配 rowSelector 的元素即可拖行, 行上用 data-sort-key 标识;
 * 拖动中直接挪 DOM, 松手 (且位移超过阈值) 后按当前 DOM 顺序回调 onDrop(keys).
 * 行内 a/button/input/label 不发起拖拽. deps 变化 (行增删 / 重排) 时重新绑定.
 */
export function useRowDragSort(
    boxRef: RefObject<HTMLElement>,
    rowSelector: string,
    onDrop: (keys: string[]) => void,
    deps: readonly unknown[],
) {
    // onDrop 走 ref: 调用方传内联函数时不必每次渲染都重绑监听
    const onDropRef = useRef(onDrop);
    onDropRef.current = onDrop;
    useEffect(() => {
        const box = boxRef.current;
        if (!box) return;
        const rows = () =>
            Array.from(box.querySelectorAll<HTMLElement>(rowSelector));
        const cleanups: Array<() => void> = [];
        rows().forEach((row) => {
            const start = (e: Event) => {
                if ((e.target as HTMLElement)?.closest('a,button,input,label'))
                    return;
                // 防止拖选文本 / 触屏滚动
                e.preventDefault();
                const te0 = e as TouchEvent;
                const startY = te0.touches
                    ? te0.touches[0].clientY
                    : (e as MouseEvent).clientY;
                // moved: 位移超过阈值才算拖拽 —— 单纯点一下不该触发排序落库
                let moved = false;
                const move = (ev: Event) => {
                    if (ev.cancelable) ev.preventDefault();
                    const te = ev as TouchEvent;
                    const y = te.touches
                        ? te.touches[0].clientY
                        : (ev as MouseEvent).clientY;
                    if (!moved && Math.abs(y - startY) > dragThresholdPx) {
                        moved = true;
                        row.classList.add('dragging'); // 真的开始拖了才变半透明
                    }
                    for (const r of rows()) {
                        if (r === row) continue;
                        const b = r.getBoundingClientRect();
                        if (y > b.top && y < b.bottom) {
                            const l = rows();
                            if (l.indexOf(row) < l.indexOf(r)) r.after(row);
                            else r.before(row);
                            break;
                        }
                    }
                };
                const up = () => {
                    document.removeEventListener('mousemove', move);
                    document.removeEventListener('mouseup', up);
                    document.removeEventListener('touchmove', move);
                    document.removeEventListener('touchend', up);
                    row.classList.remove('dragging');
                    if (!moved) return;
                    onDropRef.current(
                        rows()
                            .map((r) => r.dataset.sortKey || '')
                            .filter(Boolean),
                    );
                };
                document.addEventListener('mousemove', move);
                document.addEventListener('mouseup', up);
                document.addEventListener('touchmove', move, {
                    passive: false,
                });
                document.addEventListener('touchend', up);
            };
            row.addEventListener('mousedown', start);
            row.addEventListener('touchstart', start, { passive: false });
            cleanups.push(() => {
                row.removeEventListener('mousedown', start);
                row.removeEventListener('touchstart', start);
            });
        });
        return () => cleanups.forEach((fn) => fn());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rowSelector, ...deps]);
}
